import { makeAutoObservable } from 'mobx';
import createStoreContext from '../createStoreContext';

import {
  getAutoIngestedMemoriesCount,
  getOrgGraphBrainMemories,
} from 'src/services/brainMemory';
import { ROLES } from 'src/constants/Role';
import { BrainMemoryCount, MemoryRelation } from 'src/types/brainMemory';
import { UserApiResponse } from 'src/types/user';

export const rolesForAutoMemoryAdminOverview: Array<keyof typeof ROLES> = [
  ROLES.ADMIN,
  ROLES.SUPER_ADMIN,
  ROLES.DOMAIN_OWNER,
];

export const [BrainMemoryStoreProvider, useBrainMemoryStore] =
  createStoreContext(
    class BrainMemoryStore {
      userMemoryCount: BrainMemoryCount = {
        nodeCount: 0,
        relationshipCount: 0,
        vectorCount: 0,
      };
      orgMemoryCount: BrainMemoryCount = {
        nodeCount: 0,
        relationshipCount: 0,
        vectorCount: 0,
      };
      orgGraphMemories: MemoryRelation[] = [];

      public constructor() {
        makeAutoObservable(this);
      }

      getMemoryCountForOrg = async () => {
        const {
          success: orgMemoryCountSuccess,
          content: orgMemoryCount,
          message: orgMemoryCountMessage,
        } = await getAutoIngestedMemoriesCount('ORG');

        if (!orgMemoryCountSuccess || !orgMemoryCount) {
          throw new Error(orgMemoryCountMessage);
        } else {
          this.orgMemoryCount = {
            nodeCount: orgMemoryCount.node_count ?? 0,
            relationshipCount: orgMemoryCount.relationship_count ?? 0,
            vectorCount: orgMemoryCount.vector_count ?? 0,
          };
        }
      };

      getMemoryCountForUser = async () => {
        const {
          success: userMemoryCountSuccess,
          content: userMemoryCount,
          message: userMemoryCountMessage,
        } = await getAutoIngestedMemoriesCount('USER');

        if (!userMemoryCountSuccess || !userMemoryCount) {
          throw new Error(userMemoryCountMessage);
        } else {
          this.userMemoryCount = {
            nodeCount: userMemoryCount.node_count ?? 0,
            relationshipCount: userMemoryCount.relationship_count ?? 0,
            vectorCount: userMemoryCount.vector_count ?? 0,
          };
        }
      };

      fetchMemoryCountOverview = async (
        userDetails: Pick<UserApiResponse, '_id' | 'organizationId' | 'role'>,
      ) => {
        const promises = [];

        // organization memory count promise for admin roles
        if (rolesForAutoMemoryAdminOverview.includes(userDetails.role)) {
          promises.push(this.getMemoryCountForOrg());
        }

        // user memory count for all role
        promises.push(this.getMemoryCountForUser());

        // Execute all promises concurrently
        await Promise.all(promises);
      };

      fetchOrgGraphMemory = async () => {
        const {
          success: orgGraphMemorySuccess,
          content: orgGraphMemory,
          message: orgGraphMemoryMessage,
        } = await getOrgGraphBrainMemories();

        if (!orgGraphMemorySuccess || !orgGraphMemory) {
          throw new Error(orgGraphMemoryMessage);
        } else {
          this.orgGraphMemories = orgGraphMemory;
        }
      };
    },
  );
